import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import { monthsInCycle } from '@etiquetachef/types';
import {
  subscribeLifetimeFreeTenants,
  type LifetimeFreeListResult,
  type LifetimeFreeTenantRow,
} from '@/lib/firestoreTenantDetail';
import { isReportExcludedTenant } from '@/features/tenants/testTenantFilter';

/**
 * Card "Tenants isentos" do `/dashboard/finance`: total de cortesias vitalícias, quebra por
 * categoria e receita mensal "aberta mão" por moeda; "Ver lista" leva a
 * `/billing/promotions?tab=lifetime`.
 *
 * Origem: sem issue — commit a13ac9110, 2026-05-26 01:00 +0200 (Sprint 35 PR H2).
 *
 * Por quê: cortesia não entra no MRR (#738, #679 D15), então o painel precisa de um lugar que
 * mostre quanto ela custa — é a única visão agregada dos isentos fora da lista de promoções.
 *
 * Contrato: assina `subscribeLifetimeFreeTenants` (`lib/firestoreTenantDetail.ts`) ao montar;
 * estados LOADING/ERROR/READY. Agrega em memória (`aggregate`): conta por `category` e soma o
 * equivalente mensal `round(totalAmountCents / monthsInCycle(billingCycle))` por moeda. Sem
 * efeito colateral. Exibe `CCY 0.00` cru (não passa por `formatCurrencyCents`) — CENTAVOS.
 * Tenant de TESTE (#1008 — cadastro `testTenants.ts` OU `livemode === false`, por
 * `isReportExcludedTenant`) não entra em total, categorias nem receita aberta mão: é relatório.
 * A lista de "Ver lista" (`PromotionsPage`, operacional) continua mostrando todos.
 *
 * Usado por: `FinanceDashboardPage` (web `apps/admin`, super-admin).
 *
 * Dados: collection group `billing` com `commercial_mode == COURTESY` ordenado por
 * `courtesy.applied_at` e, para o legado, `discount.is_lifetime_free == true` ordenado por
 * `discount.lifetime_free_applied_at` — índices `billing: commercial_mode +
 * courtesy.applied_at` e `billing: discount.is_lifetime_free + discount.lifetime_free_applied_at`
 * em `firebase/firestore.indexes.json`; leitura autorizada por
 * `match /{path=**}/billing/{billingDoc}` (`isSuperAdmin()`) em `firebase/firestore.rules`.
 * Linha: `LifetimeFreeTenantRow` (`tenantId`, `category`, `totalAmountCents`, `billingCycle`,
 * `currency`).
 *
 * Dicas: a "receita perdida" é estimativa pelo valor congelado no doc de assinatura do tenant,
 * não o preço de catálogo de hoje (pricing congelado por tenant).
 *
 * Falhas conhecidas: até 2026-10-19 (#1008) os tenants de teste com cortesia (simulador,
 * IT-98765432103 e BR-01010001000180) contavam como isentos e somavam na receita aberta mão.
 *
 * Issues: #679 (D13 — ciclos trimestral/semestral; D15 — régua do MRR), #700, #738 (cortesia
 * como tipo comercial), #1008 (tenant de teste fora).
 *
 * Histórico:
 *  - 2026-06-09 00:37 +0200 — (39-G) migração para Cloudscape.
 *  - 2026-09-22 23:47 +0200 — (#700, #679) mensal pelo `monthsInCycle`, não "anual ou mês".
 *  - 2026-10-19 20:57 +0200 — (#1008) exclui tenant de teste.
 */
export function LifetimeFreeCard(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<LifetimeFreeListResult>({ kind: 'LOADING' });

  useEffect(() => {
    setState({ kind: 'LOADING' });
    return subscribeLifetimeFreeTenants(setState);
  }, []);

  const aggregates = useMemo(() => aggregate(state), [state]);

  return (
    <Container
      header={
        <Header
          variant="h2"
          actions={
            <Button
              onClick={(): void => {
                void navigate('/billing/promotions?tab=lifetime');
              }}
            >
              {t('finance.lifetimeFree.viewAll')}
            </Button>
          }
        >
          {t('finance.lifetimeFree.title')}
        </Header>
      }
    >
      {state.kind === 'LOADING' && <Spinner />}
      {state.kind === 'ERROR' && <Alert type="error">{t('finance.lifetimeFree.error')}</Alert>}
      {state.kind === 'READY' && aggregates !== null && (
        <SpaceBetween size="m">
          <div>
            <Box variant="h1">{aggregates.total}</Box>
            <Box fontSize="body-s" color="text-status-inactive">
              {t('finance.lifetimeFree.totalLabel')}
            </Box>
          </div>
          {aggregates.total > 0 ? (
            <>
              <div>
                <Box variant="awsui-key-label">{t('finance.lifetimeFree.byCategory')}</Box>
                <SpaceBetween direction="horizontal" size="xs">
                  {Object.entries(aggregates.byCategory).map(([cat, n]) => (
                    <Badge key={cat} color="grey">
                      {t(`promotions.lifetime.category.${cat}`, { defaultValue: cat })}: {n}
                    </Badge>
                  ))}
                </SpaceBetween>
              </div>
              <div>
                <Box variant="awsui-key-label">{t('finance.lifetimeFree.foregoneRevenue')}</Box>
                <SpaceBetween direction="horizontal" size="m">
                  {Object.entries(aggregates.foregoneMonthlyByCurrency).map(([currency, cents]) => (
                    <Box key={currency}>
                      <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {currency} {(cents / 100).toFixed(2)}
                      </span>
                      <Box variant="span" fontSize="body-s" color="text-status-inactive">
                        {' '}/{t('finance.lifetimeFree.perMonth')}
                      </Box>
                    </Box>
                  ))}
                </SpaceBetween>
                <Box fontSize="body-s" color="text-status-inactive">
                  {t('finance.lifetimeFree.foregoneHint')}
                </Box>
              </div>
            </>
          ) : (
            <Box color="text-body-secondary">{t('finance.lifetimeFree.empty')}</Box>
          )}
        </SpaceBetween>
      )}
    </Container>
  );
}

type Aggregates = {
  total: number;
  byCategory: Record<string, number>;
  foregoneMonthlyByCurrency: Record<string, number>;
};

/**
 * Conta por categoria e soma o equivalente mensal em centavos por moeda; `null` fora de READY.
 * Tenant de teste fica fora (#1008).
 */
function aggregate(state: LifetimeFreeListResult): Aggregates | null {
  if (state.kind !== 'READY') return null;
  const byCategory: Record<string, number> = {};
  const foregoneMonthlyByCurrency: Record<string, number> = {};
  const rows = state.rows.filter((row) => !isReportExcludedTenant(row.tenantId, row));
  for (const row of rows) {
    byCategory[row.category] = (byCategory[row.category] ?? 0) + 1;
    // Equivalente mensal pelos meses do ciclo (#679 D13), não "anual ou mês".
    const monthlyCents = Math.round(row.totalAmountCents / monthsInCycle(row.billingCycle));
    foregoneMonthlyByCurrency[row.currency] =
      (foregoneMonthlyByCurrency[row.currency] ?? 0) + monthlyCents;
  }
  return { total: rows.length, byCategory, foregoneMonthlyByCurrency };
}

/** Re-export de conveniência; nenhum consumidor encontrado em 2026-10-04 (ver #796). */
export type { LifetimeFreeTenantRow };
